import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Shield, User, Building2, FileText, ChevronDown, ChevronRight,
  Plus, Trash2, Edit2, Check, X, Loader2, RefreshCw, Upload
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const TIER_CONFIG = {
  bronze: { 
    icon: User, 
    label: 'Bronze Tier', 
    color: '#F97316',
    description: 'Basic user profile verification'
  },
  silver: { 
    icon: Building2, 
    label: 'Silver Tier', 
    color: '#6B7280',
    description: 'Company and business details'
  },
  gold: { 
    icon: Shield, 
    label: 'Gold Tier', 
    color: '#F59E0B',
    description: 'KYC/KYB document verification'
  }
};

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'url', label: 'URL' },
  { value: 'select', label: 'Dropdown' },
  { value: 'country', label: 'Country Select' },
  { value: 'file', label: 'File Upload' },
  { value: 'image_upload', label: 'Image Upload' },
  { value: 'account_type_select', label: 'Account Type Select' },
  { value: 'company_type_select', label: 'Company Type Select' },
  { value: 'language_select', label: 'Language Select' },
  { value: 'timezone_select', label: 'Timezone Select' }
];

const AdminTierQuestions = () => {
  const { user } = useAuth();
  const [tierData, setTierData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedTiers, setExpandedTiers] = useState({ bronze: true, silver: true, gold: true });
  const [expandedSections, setExpandedSections] = useState({});
  const [editingSection, setEditingSection] = useState(null);
  const [editingSectionName, setEditingSectionName] = useState('');
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [showAddModal, setShowAddModal] = useState(null); // tier name when adding

  const isMainAdmin = user?.role === 'admin' || user?.role === 'main_admin' || user?.role === 'super_admin';

  useEffect(() => {
    fetchTierQuestions();
  }, []);

  const fetchTierQuestions = async () => {
    try {
      const res = await api.get('/admin/tier-questions');
      setTierData(res.data);
      
      // Auto-expand all sections
      const sections = {};
      Object.keys(res.data).forEach(tier => {
        res.data[tier].sections.forEach(s => {
          sections[`${tier}-${s.section_key}`] = true;
        });
      });
      setExpandedSections(sections);
    } catch (error) {
      console.error('Failed to fetch tier questions:', error);
      // If no data, show seed option
      if (error.response?.status === 404 || !tierData) {
        setTierData({ bronze: { sections: [] }, silver: { sections: [] }, gold: { sections: [] } });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSeedQuestions = async () => {
    setSeeding(true);
    try {
      await api.post('/admin/tier-questions/seed');
      toast.success('Default tier questions seeded successfully');
      fetchTierQuestions();
    } catch (error) {
      if (error.response?.data?.detail?.includes('already exist')) {
        toast.error('Questions already exist. Use reset to reseed.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to seed questions');
      }
    } finally {
      setSeeding(false);
    }
  };

  const handleResetQuestions = async () => {
    if (!window.confirm('This will delete all tier questions and reset to defaults. Are you sure?')) {
      return;
    }
    
    setSeeding(true);
    try {
      await api.post('/admin/tier-questions/reset');
      toast.success('Tier questions reset to defaults');
      fetchTierQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset questions');
    } finally {
      setSeeding(false);
    }
  };

  const handleUpdateQuestion = async (questionId, updates) => {
    try {
      await api.put(`/admin/tier-questions/${questionId}`, updates);
      toast.success('Question updated');
      fetchTierQuestions();
      setEditingQuestion(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update question');
    }
  };

  const handleUpdateSectionName = async (tier, sectionKey) => {
    if (!editingSectionName.trim()) {
      setEditingSection(null);
      return;
    }
    
    try {
      await api.put(`/admin/tier-questions/section/${tier}/${sectionKey}`, { name: editingSectionName });
      toast.success('Section name updated');
      fetchTierQuestions();
      setEditingSection(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update section');
    }
  };

  const handleArchiveQuestion = async (questionId) => {
    if (!window.confirm('Archive this question? It will be hidden from users.')) return;
    
    try {
      await api.delete(`/admin/tier-questions/${questionId}`);
      toast.success('Question archived');
      fetchTierQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive question');
    }
  };

  const handleAddQuestion = async (formData) => {
    try {
      await api.post('/admin/tier-questions', formData);
      toast.success('Question added');
      fetchTierQuestions();
      setShowAddModal(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add question');
    }
  };

  const toggleTier = (tier) => {
    setExpandedTiers(prev => ({ ...prev, [tier]: !prev[tier] }));
  };

  const toggleSection = (tier, sectionKey) => {
    const key = `${tier}-${sectionKey}`;
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const startEditingSection = (tier, sectionKey, currentLabel) => {
    setEditingSection(`${tier}-${sectionKey}`);
    setEditingSectionName(currentLabel);
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </Sidebar>
    );
  }

  const hasNoQuestions = !tierData || 
    (tierData.bronze?.sections?.length === 0 && 
     tierData.silver?.sections?.length === 0 && 
     tierData.gold?.sections?.length === 0);

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tier Verification Questions</h1>
            <p className="text-gray-500 mt-1">
              Manage questions and document requirements for Bronze, Silver, and Gold verification tiers
            </p>
          </div>
          
          <div className="flex gap-3">
            {hasNoQuestions ? (
              <button
                onClick={handleSeedQuestions}
                disabled={seeding}
                className="flex items-center gap-2 px-4 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 disabled:opacity-50"
                data-testid="seed-tier-questions-btn"
              >
                {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Load Default Questions
              </button>
            ) : (
              <button
                onClick={handleResetQuestions}
                disabled={seeding}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 disabled:opacity-50"
                data-testid="reset-tier-questions-btn"
              >
                {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Reset to Defaults
              </button>
            )}
          </div>
        </div>

        {hasNoQuestions ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Tier Questions Configured</h3>
            <p className="text-gray-500 mb-6">Load the default questions to get started with tier verification management.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {['bronze', 'silver', 'gold'].map(tier => {
              const config = TIER_CONFIG[tier];
              const TierIcon = config.icon;
              const sections = tierData[tier]?.sections || [];
              
              return (
                <div key={tier} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  {/* Tier Header */}
                  <button
                    onClick={() => toggleTier(tier)}
                    className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
                    data-testid={`tier-header-${tier}`}
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className="p-3 rounded-xl"
                        style={{ backgroundColor: `${config.color}15` }}
                      >
                        <TierIcon className="w-6 h-6" style={{ color: config.color }} />
                      </div>
                      <div className="text-left">
                        <h2 className="text-lg font-semibold text-gray-900">{config.label}</h2>
                        <p className="text-sm text-gray-500">{config.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">
                        {sections.reduce((acc, s) => acc + s.questions.length, 0)} questions
                      </span>
                      {expandedTiers[tier] ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </button>
                  
                  {/* Tier Content */}
                  {expandedTiers[tier] && (
                    <div className="border-t border-gray-100">
                      {/* Add Question Button */}
                      <div className="px-6 py-3 bg-gray-50/30 border-b border-gray-100">
                        <button
                          onClick={() => setShowAddModal(tier)}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                          data-testid={`add-question-${tier}`}
                        >
                          <Plus className="w-4 h-4" />
                          Add Question to {config.label}
                        </button>
                      </div>
                      
                      {sections.map(section => {
                        const sectionKey = `${tier}-${section.section_key}`;
                        const isExpanded = expandedSections[sectionKey];
                        const isEditingThisSection = editingSection === sectionKey;
                        
                        return (
                          <div key={section.section_key} className="border-b border-gray-100 last:border-b-0">
                            {/* Section Header */}
                            <div className="flex items-center justify-between px-6 py-4 bg-gray-50/50">
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => toggleSection(tier, section.section_key)}
                                  className="p-1 hover:bg-gray-200 rounded"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-500" />
                                  )}
                                </button>
                                
                                {isEditingThisSection ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={editingSectionName}
                                      onChange={(e) => setEditingSectionName(e.target.value)}
                                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-orange-500"
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleUpdateSectionName(tier, section.section_key);
                                        if (e.key === 'Escape') setEditingSection(null);
                                      }}
                                    />
                                    <button
                                      onClick={() => handleUpdateSectionName(tier, section.section_key)}
                                      className="p-1 text-green-600 hover:bg-green-100 rounded"
                                    >
                                      <Check className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => setEditingSection(null)}
                                      className="p-1 text-gray-500 hover:bg-gray-200 rounded"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <span 
                                    className="font-medium text-gray-700 cursor-pointer hover:text-orange-600"
                                    onClick={() => startEditingSection(tier, section.section_key, section.section_label)}
                                    title="Click to edit section name"
                                  >
                                    {section.section_label}
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-gray-500">{section.questions.length} fields</span>
                            </div>
                            
                            {/* Questions List */}
                            {isExpanded && (
                              <div className="divide-y divide-gray-100">
                                {section.questions.map(question => (
                                  <QuestionRow
                                    key={question.id}
                                    question={question}
                                    isEditing={editingQuestion === question.id}
                                    onStartEdit={() => setEditingQuestion(question.id)}
                                    onCancelEdit={() => setEditingQuestion(null)}
                                    onSave={(updates) => handleUpdateQuestion(question.id, updates)}
                                    onArchive={() => handleArchiveQuestion(question.id)}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add Question Modal */}
        {showAddModal && (
          <AddQuestionModal
            tier={showAddModal}
            sections={tierData[showAddModal]?.sections || []}
            onClose={() => setShowAddModal(null)}
            onSave={handleAddQuestion}
          />
        )}
      </div>
    </Sidebar>
  );
};

// Add Question Modal Component
const AddQuestionModal = ({ tier, sections, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    tier: tier,
    section: sections[0]?.section_key || 'general',
    section_label: sections[0]?.section_label || 'General',
    field_key: '',
    label: '',
    field_type: 'text',
    placeholder: '',
    is_required: true,
    is_document_upload: false,
    display_order: 99
  });
  const [newSection, setNewSection] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.field_key || !formData.label) {
      toast.error('Field key and label are required');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Add Question to {TIER_CONFIG[tier]?.label}</h3>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Section Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
            {!newSection ? (
              <div className="flex gap-2">
                <select
                  value={formData.section}
                  onChange={(e) => {
                    const sec = sections.find(s => s.section_key === e.target.value);
                    setFormData({ ...formData, section: e.target.value, section_label: sec?.section_label || e.target.value });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {sections.map(s => (
                    <option key={s.section_key} value={s.section_key}>{s.section_label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setNewSection(true)}
                  className="px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 rounded-lg"
                >
                  + New
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Section Key (e.g., personal_info)"
                  value={formData.section}
                  onChange={(e) => setFormData({ ...formData, section: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <input
                  type="text"
                  placeholder="Section Label (e.g., Personal Information)"
                  value={formData.section_label}
                  onChange={(e) => setFormData({ ...formData, section_label: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button
                  type="button"
                  onClick={() => setNewSection(false)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Use existing section
                </button>
              </div>
            )}
          </div>

          {/* Field Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Field Key *</label>
            <input
              type="text"
              value={formData.field_key}
              onChange={(e) => setFormData({ ...formData, field_key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
              placeholder="e.g., company_name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
            <input
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              placeholder="e.g., Company Name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Field Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
            <select
              value={formData.field_type}
              onChange={(e) => setFormData({ ...formData, field_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {FIELD_TYPES.map(ft => (
                <option key={ft.value} value={ft.value}>{ft.label}</option>
              ))}
            </select>
          </div>

          {/* Placeholder */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Placeholder</label>
            <input
              type="text"
              value={formData.placeholder}
              onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
              placeholder="Optional placeholder text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Checkboxes */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_required}
                onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-orange-600"
              />
              <span className="text-sm text-gray-700">Required</span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_document_upload}
                onChange={(e) => setFormData({ ...formData, is_document_upload: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-orange-600"
              />
              <span className="text-sm text-gray-700">Document Upload</span>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              Add Question
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Question Row Component
const QuestionRow = ({ question, isEditing, onStartEdit, onCancelEdit, onSave, onArchive }) => {
  const [formData, setFormData] = useState({
    label: question.label,
    is_required: question.is_required,
    is_document_upload: question.is_document_upload || false,
    placeholder: question.placeholder || ''
  });

  const handleSave = () => {
    onSave(formData);
  };

  if (isEditing) {
    return (
      <div className="px-6 py-4 bg-orange-50/50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
            <input
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Placeholder</label>
            <input
              type="text"
              value={formData.placeholder}
              onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>
        
        <div className="flex items-center gap-6 mt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_required}
              onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-orange-600"
            />
            <span className="text-sm text-gray-700">Required</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_document_upload}
              onChange={(e) => setFormData({ ...formData, is_document_upload: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-orange-600"
            />
            <span className="text-sm text-gray-700">Requires Document Upload</span>
          </label>
        </div>
        
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onCancelEdit}
            className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-3 py-1.5 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700"
          >
            Save Changes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-3 flex items-center justify-between hover:bg-gray-50 group">
      <div className="flex items-center gap-4">
        <div className="w-8 text-center">
          {question.is_document_upload ? (
            <Upload className="w-4 h-4 text-blue-500 mx-auto" />
          ) : (
            <FileText className="w-4 h-4 text-gray-400 mx-auto" />
          )}
        </div>
        <div>
          <span className="text-sm text-gray-900">{question.label}</span>
          {question.is_required && <span className="text-red-500 ml-1">*</span>}
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-gray-400">{question.field_key}</span>
            <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{question.field_type}</span>
            {question.is_document_upload && (
              <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded">Upload</span>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={onStartEdit}
          className="p-1.5 text-gray-500 hover:bg-gray-200 rounded"
          title="Edit question"
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={onArchive}
          className="p-1.5 text-red-500 hover:bg-red-100 rounded"
          title="Archive question"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default AdminTierQuestions;
